import React, { useState, useMemo } from 'react';
import { 
    Box, 
    Tabs, 
    Tab, 
    Typography, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Button, 
    TextField, 
    FormControlLabel, 
    Switch, 
    Card,
    CardContent,
    CardHeader,
    Alert,
    LinearProgress,
    Paper,
    Grid,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import { 
    UploadFile, 
    Event, 
    Receipt, 
    Payment
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import FileUploadDropbox from '../../components/dialogs/FileUploadDropbox';
import axios from 'axios';
import useDialog from '../../hooks/useDialog';
import dateFormat from 'dateformat';
import SchedulingLogs from './SchedulingLogs';

const COMPONENTS = [
    { value: 'universal', label: 'უნივერსალური' },
    { value: 'new_universal', label: 'ახალი უნივერსალური' },
    { value: 'mortgage', label: 'იპოთეკა' },
    { value: 'legacy_mortgage', label: 'ძველი იპოთეკა' }
];

export default function Scheduling() {
    const [activeTab, setActiveTab] = useState(0);
    const [component, setComponent] = useState('universal');
    const [loanId, setLoanId] = useState('');
    const [loading, setLoading] = useState(false);
    const { showSnackbar } = useDialog();
    const [refreshLogs, setRefreshLogs] = useState(0);
    const [resultDialog, setResultDialog] = useState<{ open: boolean, title: string, message: string, type: 'success' | 'error' }>({ open: false, title: '', message: '', type: 'success' });

    // Tab 0: Schedule
    const [scheduleData, setScheduleData] = useState<any[]>([]);
    const [overwriteDiscrepancy, setOverwriteDiscrepancy] = useState(false);

    // Tab 1: Due Dates
    const [dueDays, setDueDays] = useState('');
    const [dueDate, setDueDate] = useState('');

    // Tab 2: Request Payment
    const [paymentDates, setPaymentDates] = useState('');
    const [acceptCalculation, setAcceptCalculation] = useState(false);

    // Tab 3: Submit Payment
    const [submitPaymentDate, setSubmitPaymentDate] = useState('');
    const [interestAmount, setInterestAmount] = useState('');
    const [principalAmount, setPrincipalAmount] = useState('');

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleComponentChange = (event: any) => {
        setComponent(event.target.value);
        setScheduleData([]);
        // Keep Loan ID
    };

    const handleUploadSuccess = (data: any) => {
        if (data.error) {
            showSnackbar(data.message || 'შეცდომა ფაილის დამუშავებისას', 'error');
            return;
        }
        setScheduleData(data.schedule_data || []);
        if (data.loan_id) {
            setLoanId(data.loan_id);
        }
        showSnackbar('ფაილი წარმატებით დამუშავდა', 'success');
    };

    const handleUploadError = (err: any) => {
        console.error(err);
        showSnackbar('შეცდომა ფაილის ატვირთვისას', 'error');
    };

    const formatToDDMMYYYY = (dateString: string) => {
        if (!dateString) return '';
        try {
            return dateFormat(new Date(dateString), 'dd/mm/yyyy');
        } catch (e) {
            return dateString;
        }
    };

    const submitRequest = (url: string, data: any) => {
        setLoading(true);
        axios.post(url, data)
        .then((res) => {
            setLoading(false);
            setResultDialog({
                open: true,
                title: 'წარმატება',
                message: res.data.message || 'ოპერაცია წარმატებით შესრულდა',
                type: 'success'
            });
            setRefreshLogs(prev => prev + 1);
        })
        .catch((err) => {
            setLoading(false);
            let message = 'შეცდომა კავშირისას';
            if (err.response) {
                message = err.response.data.message || 'შეცდომა';
            }
            setResultDialog({
                open: true,
                title: 'შეცდომა',
                message: message,
                type: 'error'
            });
            setRefreshLogs(prev => prev + 1);
        });
    };

    const handleSubmitSchedule = () => {
        if (!loanId) return showSnackbar('გთხოვთ მიუთითოთ სესხის ID', 'error');
        if (scheduleData.length === 0) return showSnackbar('გთხოვთ ატვირთოთ გრაფიკი', 'error');

        submitRequest(`/bank/web/${component}/submit_schedule`, {
            loan_id: loanId,
            overwrite_discrepancy: overwriteDiscrepancy ? 'True' : 'False',
            schedule_data: scheduleData
        });
    };

    const handleSubmitDueDates = () => {
        if (!loanId) return showSnackbar('გთხოვთ მიუთითოთ სესხის ID', 'error');
        if (!dueDays || !dueDate) return showSnackbar('შეავსეთ ყველა ველი', 'error');

        submitRequest(`/bank/web/${component}/submit_due_dates`, {
            loan_id: loanId,
            due_days: parseInt(dueDays),
            date: formatToDDMMYYYY(dueDate)
        });
    };

    const handleRequestPayment = () => {
        if (!loanId) return showSnackbar('გთხოვთ მიუთითოთ სესხის ID', 'error');
        if (!paymentDates) return showSnackbar('მიუთითეთ თარიღები', 'error');

        const datesArray = paymentDates.split(/[\n,]+/).map(d => d.trim()).filter(d => d);

        submitRequest(`/bank/web/${component}/request_payment`, {
            loan_id: loanId,
            dates: datesArray, 
            accept_calculation: acceptCalculation
        });
    };

    const handleSubmitPayment = () => {
        if (!loanId) return showSnackbar('გთხოვთ მიუთითოთ სესხის ID', 'error');
        if (!submitPaymentDate || !interestAmount || !principalAmount) return showSnackbar('შეავსეთ ყველა ველი', 'error');

        submitRequest(`/bank/web/${component}/submit_payment`, {
            loan_id: loanId,
            date: formatToDDMMYYYY(submitPaymentDate),
            interest_amount: parseFloat(interestAmount),
            principal_amount: parseFloat(principalAmount)
        });
    };

    const columns = useMemo(() => {
        const commonCols: GridColDef[] = [
            { field: 'payment_date', headerName: 'გადახდის თარიღი', width: 150 },
            { field: 'tranche_amount', headerName: 'ტრანშის ოდენობა', width: 150, type: 'number' },
        ];

        if (component === 'universal' || component === 'new_universal') {
            return [
                ...commonCols,
                { field: 'tranche_date', headerName: 'ტრანშის თარიღი', width: 150 },
                { field: 'refinancing_rate', headerName: 'რეფინანსირების განაკვეთი', width: 150, type: 'number' },
                { field: 'interest_rate', headerName: 'საპროცენტო განაკვეთი', width: 150, type: 'number' },
                { field: 'initial_balance', headerName: 'საწყისი ნაშთი', width: 150, type: 'number' },
                { field: 'final_balance', headerName: 'საბოლოო ნაშთი', width: 150, type: 'number' },
                { field: 'total_payment', headerName: 'სულ გადასახდელი', width: 150, type: 'number' },
                { field: 'principal', headerName: 'ძირი', width: 150, type: 'number' },
                { field: 'interest', headerName: 'პროცენტი', width: 150, type: 'number' },
                { field: 'client_share', headerName: 'კლიენტის წილი', width: 150, type: 'number' },
                { field: 'agency_share', headerName: 'სააგენტოს წილი', width: 150, type: 'number' },
            ];
        } else if (component === 'mortgage') {
            return [
                ...commonCols,
                { field: 'refinancing_rate', headerName: 'რეფინანსირების განაკვეთი', width: 150, type: 'number' },
                { field: 'subsidy_rate', headerName: 'სუბსიდირების განაკვეთი', width: 150, type: 'number' },
                { field: 'children_amount', headerName: 'შვილების რაოდენობა', width: 150, type: 'number' },
                { field: 'interest_rate', headerName: 'საპროცენტო განაკვეთი', width: 150, type: 'number' },
                { field: 'initial_balance', headerName: 'საწყისი ნაშთი', width: 150, type: 'number' },
                { field: 'final_balance', headerName: 'საბოლოო ნაშთი', width: 150, type: 'number' },
                { field: 'total_payment', headerName: 'სულ გადასახდელი', width: 150, type: 'number' },
                { field: 'principal', headerName: 'ძირი', width: 150, type: 'number' },
                { field: 'interest', headerName: 'პროცენტი', width: 150, type: 'number' },
                { field: 'client_share', headerName: 'კლიენტის წილი', width: 150, type: 'number' },
                { field: 'agency_share', headerName: 'სააგენტოს წილი', width: 150, type: 'number' },
                { field: 'offset', headerName: 'ოფსეტი', width: 100 },
            ];
        } else if (component === 'legacy_mortgage') {
             return [
                ...commonCols,
                { field: 'reversal_principal', headerName: 'Reversal Principal', width: 150, type: 'number' },
                { field: 'interest_type', headerName: 'პროცენტის ტიპი', width: 150 },
                { field: 'refinancing_rate', headerName: 'რეფინანსირების განაკვეთი', width: 150, type: 'number' },
                { field: 'subsidy_rate', headerName: 'სუბსიდირების განაკვეთი', width: 150, type: 'number' },
                { field: 'interest_rate', headerName: 'საპროცენტო განაკვეთი', width: 150, type: 'number' },
                { field: 'initial_balance', headerName: 'საწყისი ნაშთი', width: 150, type: 'number' },
                { field: 'final_balance', headerName: 'საბოლოო ნაშთი', width: 150, type: 'number' },
                { field: 'total_payment', headerName: 'სულ გადასახდელი', width: 150, type: 'number' },
                { field: 'principal', headerName: 'ძირი', width: 150, type: 'number' },
                { field: 'interest', headerName: 'პროცენტი', width: 150, type: 'number' },
                { field: 'client_share', headerName: 'კლიენტის წილი', width: 150, type: 'number' },
                { field: 'agency_share', headerName: 'სააგენტოს წილი', width: 150, type: 'number' },
                { field: 'offset', headerName: 'ოფსეტი', width: 100 },
             ];
        }
        return [];
    }, [component]);

    return (
        <Box sx={{ p: 3 }}>
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardHeader title="გრაფიკების მართვა" titleTypographyProps={{ variant: 'h5', fontWeight: 600 }} />
                <CardContent>
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>კომპონენტი</InputLabel>
                                <Select
                                    value={component}
                                    label="კომპონენტი"
                                    onChange={handleComponentChange}
                                >
                                    {COMPONENTS.map((comp) => (
                                        <MenuItem key={comp.value} value={comp.value}>
                                            {comp.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="სესხის ID"
                                value={loanId}
                                onChange={(e) => setLoanId(e.target.value)}
                                size="small"
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange} 
                            aria-label="scheduling tabs"
                            variant="fullWidth"
                        >
                            <Tab icon={<UploadFile />} iconPosition="start" label="გრაფიკის გაგზავნა (Submit Schedule)" />
                            <Tab icon={<Event />} iconPosition="start" label="ვადაგადაცილების განახლება Submit Due Dates" />
                            <Tab icon={<Receipt />} iconPosition="start" label="თანხის მოთხოვნა Request Payment" />
                            <Tab icon={<Payment />} iconPosition="start" label="გადახდის ასახვა Submit Payment" />
                        </Tabs>
                    </Box>

                    {/* Tab 0: Submit Schedule */}
                    {activeTab === 0 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FileUploadDropbox
                                    uploadUrl={`/bank/web/scheduling/parse?component=${component}`}
                                    onUploadSuccess={handleUploadSuccess}
                                    onUploadError={handleUploadError}
                                    promptMessage="ატვირთეთ გრაფიკის ფაილი"
                                    accept={{ 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }}
                                />
                            </Grid>

                            {scheduleData.length > 0 && (
                                <>
                                    <Grid item xs={12}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={overwriteDiscrepancy}
                                                    onChange={(e) => setOverwriteDiscrepancy(e.target.checked)}
                                                />
                                            }
                                            label="Overwrite Discrepancy"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ height: 600, width: '100%' }}>
                                            <DataGrid
                                                rows={scheduleData}
                                                columns={columns}
                                                pageSize={100}
                                                rowsPerPageOptions={[10, 25, 50, 100]}
                                                disableSelectionOnClick
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box display="flex" justifyContent="flex-end">
                                            <Button 
                                                variant="contained" 
                                                color="primary" 
                                                onClick={handleSubmitSchedule}
                                                disabled={loading}
                                            >
                                                {loading ? 'იგზავნება...' : 'გრაფიკის გაგზავნა'}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}

                    {/* Tab 1: Submit Due Dates */}
                    {activeTab === 1 && (
                        <Grid container spacing={3} maxWidth="md" sx={{ mx: 'auto' }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="ვადაგადაცილებული დღეები"
                                    type="number"
                                    value={dueDays}
                                    onChange={(e) => setDueDays(e.target.value)}
                                    InputProps={{ inputProps: { min: 0 } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="თარიღი"
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleSubmitDueDates}
                                        disabled={loading}
                                    >
                                        {loading ? 'იგზავნება...' : 'გაგზავნა'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {/* Tab 2: Request Payment */}
                    {activeTab === 2 && (
                        <Grid container spacing={3} maxWidth="md" sx={{ mx: 'auto' }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="მოთხოვნის თარიღები (DD/MM/YYYY)"
                                    multiline
                                    rows={4}
                                    value={paymentDates}
                                    onChange={(e) => setPaymentDates(e.target.value)}
                                    placeholder="მაგ: 25/12/2025"
                                    helperText="შეიყვანეთ თარიღები (DD/MM/YYYY), თითო ახალ ხაზზე ან მძიმით გამოყოფილი"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={acceptCalculation}
                                            onChange={(e) => setAcceptCalculation(e.target.checked)}
                                        />
                                    }
                                    label="დაეთანხმე კალკულაციას (Accept Calculation)"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleRequestPayment}
                                        disabled={loading}
                                    >
                                        {loading ? 'იგზავნება...' : 'მოთხოვნა'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {/* Tab 3: Submit Payment */}
                    {activeTab === 3 && (
                        <Grid container spacing={3} maxWidth="md" sx={{ mx: 'auto' }}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="გადახდის თარიღი"
                                    type="date"
                                    value={submitPaymentDate}
                                    onChange={(e) => setSubmitPaymentDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="პროცენტი"
                                    type="number"
                                    value={interestAmount}
                                    onChange={(e) => setInterestAmount(e.target.value)}
                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="ძირი"
                                    type="number"
                                    value={principalAmount}
                                    onChange={(e) => setPrincipalAmount(e.target.value)}
                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button 
                                        variant="contained" 
                                        color="primary" 
                                        onClick={handleSubmitPayment}
                                        disabled={loading}
                                    >
                                        {loading ? 'იგზავნება...' : 'გადახდის გაგზავნა'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {loading && <LinearProgress sx={{ mt: 2 }} />}
                </CardContent>
            </Card>

            <SchedulingLogs refreshTrigger={refreshLogs} />

            <Dialog
                open={resultDialog.open}
                onClose={() => setResultDialog({ ...resultDialog, open: false })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ color: resultDialog.type === 'error' ? 'error.main' : 'success.main' }}>
                    {resultDialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
                        {resultDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResultDialog({ ...resultDialog, open: false })} color="primary" autoFocus>
                        დახურვა
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
